import { z } from "zod";

import {
  MAX_LENGTH_MESSAGE,
  MIN_LENGTH_MESSAGE,
  REQUIRED_MESSAGE,
} from "@/utils";

import { EProductType } from "../products.type";

// Schema base comum para todos os produtos
export const baseProductSchema = z.object({
  // Tab Data - Campos comuns
  type: z.nativeEnum(EProductType),
  name: z
    .string()
    .min(2, { message: MIN_LENGTH_MESSAGE("2") })
    .max(150, { message: MAX_LENGTH_MESSAGE("150") }),
  insurance_company_id: z.string().uuid({ message: REQUIRED_MESSAGE }),
  payment_gateway_id: z.string().uuid({ message: REQUIRED_MESSAGE }),
  policy_number: z.string().min(1, { message: REQUIRED_MESSAGE }),
  cod_sub: z.string().optional().nullable(),
  susep_code: z.string().min(1, { message: REQUIRED_MESSAGE }),
  iof_value: z
    .preprocess((v) => (v === "" || v === null ? 0 : v), z.coerce.number())
    .default(0),
  type_of_vigency: z.enum(
    [
      "pluriannual",
      "annual",
      "monthly",
      "daily",
      "pay_per_use",
      "lifetime",
    ],
    {
      message: REQUIRED_MESSAGE,
    },
  ),
  policy_start_date: z.coerce.date({
    required_error: REQUIRED_MESSAGE,
    invalid_type_error: "Data de início da apólice inválida",
  }),
  policy_end_date: z.coerce.date({
    required_error: REQUIRED_MESSAGE,
    invalid_type_error: "Data de término da apólice inválida",
  }),
  min_permitted_age: z
    .preprocess(
      (v) =>
        v === "" || v === null || v === undefined ? undefined : Number(v),
      z.number().min(0).optional()
    )
    .optional()
    .nullable(),
  max_permitted_age: z
    .preprocess(
      (v) =>
        v === "" || v === null || v === undefined ? undefined : Number(v),
      z.number().min(0).optional()
    )
    .optional()
    .nullable(),
  // Campos específicos do Auto (opcionais no schema base)
  km_max_permitted: z
    .preprocess(
      (v) =>
        v === "" || v === null || v === undefined ? undefined : Number(v),
      z.number().min(0).optional()
    )
    .optional()
    .nullable(),
  min_manufacturing_year: z
    .preprocess(
      (v) =>
        v === "" || v === null || v === undefined ? undefined : Number(v),
      z.number().min(1900).optional()
    )
    .optional()
    .nullable(),
  description: z.string().optional(),
  insurance_coverages: z.string().optional(),
  insurance_benefits: z.string().optional(),
  general_conditions: z.string().optional(),
  external_product_id: z
    .string()
    .max(255, { message: MAX_LENGTH_MESSAGE("255") })
    .optional()
    .nullable(),
  // Aba Integração - ID da apólice permanece opcional
  insurance_policy_id: z.string().optional().nullable(),
  // Aba Integração - Campos obrigatórios
  secondary_policy_holder: z
    .string()
    .min(1, { message: REQUIRED_MESSAGE })
    .refine((val) => val !== null && val !== undefined && val.trim() !== "", {
      message: REQUIRED_MESSAGE,
    }),
  stipulant_code: z
    .string()
    .min(1, { message: REQUIRED_MESSAGE })
    .refine((val) => val !== null && val !== undefined && val.trim() !== "", {
      message: REQUIRED_MESSAGE,
    }),
  sub_stipulant_code: z
    .string()
    .min(1, { message: REQUIRED_MESSAGE })
    .refine((val) => val !== null && val !== undefined && val.trim() !== "", {
      message: REQUIRED_MESSAGE,
    }),
  table_code: z
    .string()
    .min(1, { message: REQUIRED_MESSAGE })
    .refine((val) => val !== null && val !== undefined && val.trim() !== "", {
      message: REQUIRED_MESSAGE,
    }),
  insurance_policy_extra_price_code: z.string().optional().nullable(),

  // Campos comuns de capital ranges
  capital_ranges: z
    .array(
      z.object({
        capital_value: z
          .preprocess(
            (v) => (v === "" || v === null ? undefined : v),
            z.coerce.number()
          )
          .optional(),
        premium_value: z
          .preprocess(
            (v) => (v === "" || v === null ? undefined : v),
            z.coerce.number()
          )
          .optional(),
        net_premium: z
          .preprocess(
            (v) => (v === "" || v === null ? undefined : v),
            z.coerce.number()
          )
          .optional(),
        description: z.string().optional(),
        markup: z
          .preprocess(
            (v) => (v === "" || v === null ? undefined : v),
            z.coerce.number()
          )
          .optional(),
        markup_mode: z
          .enum(["percentage", "real_currency"])
          .default("percentage"),
        commission_auto: z
          .preprocess(
            (v) => (v === "" || v === null ? undefined : v),
            z.coerce.number()
          )
          .optional(),
        code_plan: z.string().optional().nullable(),
        plan_name: z.string().optional().nullable(),
        net_price: z
          .preprocess(
            (v) => (v === "" || v === null ? undefined : v),
            z.coerce.number()
          )
          .optional(),
        minimum_price: z
          .preprocess(
            (v) => (v === "" || v === null ? undefined : v),
            z.coerce.number()
          )
          .optional(),
        maximum_price: z
          .preprocess(
            (v) => (v === "" ? undefined : v),
            z.coerce.number().nullable()
          )
          .optional(),
      })
    )
    .nonempty({ message: REQUIRED_MESSAGE }),

  // Campos comuns de comissão
  commission_distribution: z
    .array(
      z.object({
        agency: z
          .preprocess(
            (v) => (v === "" || v === null || v === undefined ? 0 : v),
            z.coerce.number()
          )
          .default(0),
        commission: z
          .preprocess(
            (v) => (v === "" || v === null || v === undefined ? 0 : v),
            z.coerce.number()
          )
          .default(0),
        pro_labore: z
          .preprocess(
            (v) => (v === "" || v === null || v === undefined ? 0 : v),
            z.coerce.number()
          )
          .default(0),
        markup: z
          .preprocess(
            (v) => (v === "" || v === null ? undefined : v),
            z.coerce.number()
          )
          .optional(),
        markup_mode: z
          .enum(["percentage", "real_currency"])
          .default("percentage"),
      })
    )
    .nonempty({ message: "Pelo menos uma comissão é obrigatória" }),

  // Campos comuns de preço
  capital_value: z
    .preprocess(
      (v) => (v === "" || v === null ? undefined : v),
      z.coerce.number()
    )
    .optional(),
  premium_value: z
    .preprocess(
      (v) => (v === "" || v === null ? undefined : v),
      z.coerce.number()
    )
    .optional(),
  net_premium: z
    .preprocess(
      (v) => (v === "" || v === null || v === undefined ? undefined : v),
      z.coerce.number().optional()
    )
    .optional(),

  // Campos comuns de comissão
  commission_unique: z
    .preprocess(
      (v) => (v === "" || v === null ? undefined : v),
      z.coerce.number()
    )
    .optional(),
  pro_labore_unique: z
    .preprocess(
      (v) => (v === "" || v === null ? undefined : v),
      z.coerce.number()
    )
    .optional(),
  commission_lifetime_other_installments: z
    .preprocess(
      (v) => (v === "" || v === null || v === undefined ? 0 : v),
      z.coerce.number()
    )
    .default(0),
  pro_labore_lifetime_other_installments: z
    .preprocess(
      (v) => (v === "" || v === null || v === undefined ? 0 : v),
      z.coerce.number()
    )
    .default(0),
  markup: z
    .preprocess(
      (v) => (v === "" || v === null ? undefined : v),
      z.coerce.number()
    )
    .optional(),
  markup_mode: z.enum(["percentage", "real_currency"]).default("percentage"),
});

// Tipos base
export type TBaseProductFormData = z.infer<typeof baseProductSchema>;
